import type { DoneInvokeEvent } from 'xstate';
import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import { ProStudioEvents, ProStudioContext } from '../prostudio.xstate.types';
import { tableStore } from '../../stores';
import { ValidationMode } from '../../stores/table-store';
import { checkForErrors } from '../../utils';
import {
  captureAnalyticsEvent,
  classifyAnalyticsError,
  solutionAnalyticsProps,
  MAX_ANALYTICS_ERROR_MESSAGE_LENGTH,
} from '../../utils/analytics';

export const reloadNodes = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<boolean> => {
  if (event.type !== 'reloadNodes') {
    return false;
  }

  const {
    cb,
    silent,
    onNotFound,
    solutionVersionId: eventSolutionVersionId,
    filenames,
  } = event.payload;

  // Best-effort, csv_uploaded/csv_upload_failed only — the silent cross-tab
  // sync (PSTUDIO-3610) is not a user-initiated CSV upload and must not fire
  // either event.
  const csvAnalyticsProps = {
    ...solutionAnalyticsProps(ctx),
    solution_version_id: eventSolutionVersionId,
    filename: filenames?.join(', '),
    file_count: filenames?.length ?? 0,
  };

  // tableStore.solutionVersionId holds the legacy string (`${customerId}-${botName}-${draftVersion}`)
  // which is the primary key for tenancy_solution_versions_ps_by_pk.
  // event.payload.solutionVersionId carries a UUID from draftVersionSolutionData.id
  // (a different table), which Hasura cannot resolve, returning null versionData.
  const { solutionVersionId } = tableStore;

  // PSTUDIO-4103: did the payload actually land in the store? Hoisted out of
  // the branch below so BOTH the `finally` and the resolved value can report
  // it. Every path leaves it defined: applied -> true; dropped by the identity
  // guard, a deleted solution, the identity throw or a transport failure ->
  // false, because in none of those did this reload change the table.
  let applied = false;
  // What the invoke RESOLVES with — see the `finally`, which narrows `applied`
  // by a second identity check before either consumer sees it.
  let settled = false;
  // Guards BOTH captures in the `finally`: once the catch or the deleted-
  // solution branch has reported a failure, this invoke must not ALSO report
  // a success, nor report a second failure. The second half is not defensive —
  // both of those branches leave `applied` false, so `landed` is false and the
  // `version_changed` capture below would fire on top of them.
  let capturedFailure = false;

  try {
    if (!solutionVersionId) {
      throwErrorWithId('Solution version ID is required');
    }

    const res = await urqlGql
      .getProStudioVersionData(
        { solutionVersionId },
        { requestPolicy: 'network-only' }
      )
      .toPromise();

    checkForErrors(res);

    const versionData = res.data?.tenancy_solution_versions_ps_by_pk;

    if (versionData) {
      // cross-tab (silent) → incremental merge (no full re-render);
      // CSV upload / other → full processVersionData rebuild.
      // PSTUDIO-3953: assert the identity this reload was issued for. The
      // fetch above is awaited, and a solution/version switch during that await
      // would otherwise apply these nodes to whatever the store now holds.
      applied = silent
        ? await tableStore.mergeVersionData(versionData, {
            skipValidation: true,
            forSolutionVersionId: solutionVersionId,
          })
        : await tableStore.processVersionData(versionData, {
            skipValidation: true,
            forSolutionVersionId: solutionVersionId,
          });

      // Validation is a consequence of data being applied. Running it after a
      // DROPPED payload would validate — and upload — a table this reload has
      // nothing to do with, at the cost of a full round trip.
      if (applied) {
        await tableStore.validateAndSync(ValidationMode.Auto);
      }
    } else {
      // PSTUDIO-3671: a definitive null for an already-loaded solutionVersionId
      // (guarded truthy above) means the solution/version was deleted — not a
      // provisioning transient (those never reach reload). Signal the caller so
      // it can dispatch `solutionNotFound` (toast + redirect) from the React
      // layer; dispatching here would create a circular import into the machine.
      //
      // PSTUDIO-3953: only when the store is still ON that version. Where
      // `onNotFound` IS supplied this branch ejects the user (toast +
      // redirect), so a late null for a version they already left would throw
      // them out of the solution they are currently working in — the identity
      // check matters more here than on the apply path, not less. Today that
      // is the cross-tab sync and nothing else; see the capture below.
      if (tableStore.solutionVersionId === solutionVersionId) {
        // A CSV upload whose target solution/version was deleted out from
        // under it — a real failure, not a no-op. Without this,
        // `csv_uploaded + csv_upload_failed < attempts` silently, and whoever
        // builds the funnel overstates the success rate.
        //
        // What the user is shown on THIS path, stated rather than assumed:
        // nothing. `onNotFound` has one production call site,
        // `use-cross-tab-node-sync.ts:202`, and it is passed together with
        // `silent: true` (`:200`) — so on every `!silent` path, i.e. every
        // path that captures, `onNotFound` is `undefined` and the optional
        // call below is a no-op. The toast-and-redirect this comment used to
        // promise belongs to the silent path, where the event is suppressed
        // by design. The event is therefore the only trace this failure
        // leaves; giving the user one is a separate change.
        if (!silent) {
          capturedFailure = true;
          captureAnalyticsEvent('csv_upload_failed', {
            ...csvAnalyticsProps,
            failure_stage: 'not_found',
          });
        }
        onNotFound?.();
      }
    }
  } catch (error) {
    // csv_upload_failed, failure_stage 'reload' — the upload itself already
    // succeeded (see use-upload-csv.ts for the 'upload' stage); this is the
    // reload-into-the-table step failing instead. Rethrown unchanged below —
    // this must not alter the existing error path (still routes to
    // #botFlowError in the machine).
    if (!silent) {
      const errorMessage = (
        error instanceof Error ? error.message : String(error)
      ).slice(0, MAX_ANALYTICS_ERROR_MESSAGE_LENGTH);

      capturedFailure = true;
      captureAnalyticsEvent('csv_upload_failed', {
        ...csvAnalyticsProps,
        failure_stage: 'reload',
        // PSTUDIO-4194: `error_message` is bounded in SIZE only and its front —
        // where row-level content sits — is the part that survives. The kind is
        // the axis a funnel can group on, as it is on the publish event.
        error_kind: classifyAnalyticsError(errorMessage),
        error_message: errorMessage,
      });
    }
    throw error;
  } finally {
    // PSTUDIO-4103: re-assert the identity at the moment the flag is HANDED
    // OVER, not only at the moment the payload was applied. `applied` is set
    // before `validateAndSync`, which awaits an upload and a server-side
    // compile — seconds — and the consumers act after that. Without this a
    // switch inside that window still handed `true` to `applyImportedGrouping`,
    // which matches by Node Number (numbers collide across solutions) and
    // writes with `saveImmediately`: the exact graft this ticket removes, just
    // through a narrower window. So the flag answers the question the
    // consumers actually ask — "did this reload land, and are we still there?"
    const landed =
      applied && tableStore.solutionVersionId === solutionVersionId;

    // csv_uploaded — mirrors `isReloadApplied` (both read the same `landed`
    // value, just via different channels: this callback vs. the invoke's
    // resolved value + XState `cond`). `!capturedFailure` keeps this
    // mutually exclusive with the catch/onNotFound failure captures above —
    // see the `capturedFailure` declaration for why that can't be assumed.
    if (!silent && landed && !capturedFailure) {
      captureAnalyticsEvent('csv_uploaded', csvAnalyticsProps);
    }

    // The second cause of a silent funnel hole, and the one the `not_found`
    // capture above does not cover: the reload SUCCEEDED but the payload was
    // dropped — either the identity guard inside `processVersionData` refused
    // it, or the store moved to another solution version inside the
    // `validateAndSync` window. No throw, no null from the backend, and the
    // user's import did not land. Unreported, `csv_uploaded +
    // csv_upload_failed < attempts` silently for exactly the case the
    // `not_found` comment argues about.
    //
    // `!capturedFailure` is what keeps this mutually exclusive with the two
    // captures above: both of them leave `applied` false, so `landed` is false
    // here and without the flag every deleted-solution reload would report
    // twice. That is what makes the assignment at the `not_found` capture
    // load-bearing rather than defensive.
    if (!silent && !landed && !capturedFailure) {
      captureAnalyticsEvent('csv_upload_failed', {
        ...csvAnalyticsProps,
        failure_stage: 'version_changed',
      });
    }

    // `cb` is CALLED on every path — its other consumer, `finishReload` in
    // use-cross-tab-node-sync, frees the in-flight flag and clears the 15s
    // watchdog, and gating the call would wedge both. What the flag does is let
    // each consumer decide: the CSV import skips grafting its group headers
    // when the payload never landed.
    cb?.(landed);
    settled = landed;
  }

  return settled;
};

/**
 * PSTUDIO-4103 / PSTUDIO-4122: named export of the `landed` flag this invoke
 * resolves with. The CSV success toast used to branch on it in the machine;
 * PSTUDIO-4122 moved that toast to `use-upload-csv.ts`, which reports from the
 * grouping graft's outcome instead. The flag is still what `cb` receives and
 * what the invoke returns, so the predicate stays exported and unit-tested.
 */
export const isReloadApplied = (
  _ctx: ProStudioContext,
  event: DoneInvokeEvent<boolean>
): boolean => event.data === true;
